// Calculator.jsx - The scrap metal calculators. One component, three routes,
// because gold, silver and platinum differ only in their purity table and their
// copy.
//
// These pages exist for two reasons. They are genuinely the most useful thing we
// can give somebody who is deciding whether to sell, and they are the search
// traffic that brings that person here in the first place.
//
// The maths runs in the browser off the live spot price so the number moves as
// you type. It shows metal content value only, never an offer, which keeps it
// honest and keeps our pricing our business.
//
// LEGEND
//   1. METALS       per-metal copy, purity table, and SEO text
//   2. Row          one line of the calculator
//   3. Calculator   the page

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";


import { Seo, JsonLd, breadcrumbSchema, faqSchema, toolSchema, SITE } from "../lib/seo.jsx";
import { useSpot, useAsync } from "../lib/hooks.js";
import * as analytics from "../lib/analytics.js";
import { publicApi } from "../lib/api.js";
import { money, moneyExact, grams, ounces, toGrams, UNITS, GRAMS_PER_OZT } from "../lib/format.js";
import { Panel, Select, Input, Alert } from "../ui/kit.jsx";
import { Plus, Trash, ArrowRight, Scale } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed, CountUp } from "../ui/motion.jsx";
import { ChainCoil, CoinStack, RingSolitaire } from "../ui/illustrations.jsx";
import { CtaBand, MobileCta, EmbedThis } from "./parts.jsx";

////////// 1. PER METAL //////////

// A small drawing per metal, so the page has something made on it.
const ART = { gold: ChainCoil, silver: CoinStack, platinum: RingSolitaire };

const METALS = {
  gold: {
    label: "Gold",
    title: "Scrap gold calculator",
    heading: "What is your scrap gold actually worth?",
    lede: "Enter the weight and the karat and this works out the pure gold content and what that content is worth at today's spot price. It updates as you type.",
    seoDescription: "Free scrap gold calculator. Work out the pure gold content of 10K, 14K, 18K, 22K and 24K jewellery and what it is worth at today's live gold price.",
    fallback: [
      { value: "10k", label: "10K (marked 417)", factor: 0.417 },
      { value: "14k", label: "14K (marked 585)", factor: 0.583 },
      { value: "18k", label: "18K (marked 750)", factor: 0.75 },
      { value: "22k", label: "22K (marked 916)", factor: 0.916 },
      { value: "24k", label: "24K (marked 999)", factor: 0.999 },
    ],
    defaultPurity: "14k",
    notes: [
      ["Where to find the mark", "Inside a ring band, on the flat of a clasp, on the post of an earring, or on the tongue of a bracelet. It reads 10K, 14K, 585, 750 and so on."],
      ["What is not gold", "Springs, clasp mechanisms, watch movements and anything magnetic. Stones and their settings are deducted separately."],
      ["Gold filled and plated", "Marked GF, RGP, HGE or 1/20. There is real gold there but it is a fraction of a percent by weight, and it is almost never worth recovering."],
    ],
    faqs: [
      { question: "Is 14K gold half gold?", answer: "Close. 14K is 14 parts gold out of 24, which is 58.3 percent by weight. So 10 grams of 14K jewellery contains about 5.83 grams of pure gold." },
      { question: "Why does my jewellery weigh more than the gold in it?", answer: "Because karat gold is an alloy. The rest is copper, silver, nickel or zinc, which give it colour and hardness. Only the gold fraction carries the value." },
      { question: "Do you pay the number this calculator shows?", answer: "No, and neither does anyone else. That figure is the raw metal content at spot. Every buyer has to refine, insure, ship and carry price risk, so every offer sits below it. Use this number to compare offers on the same basis." },
    ],
  },

  silver: {
    label: "Silver",
    title: "Silver calculator",
    heading: "What is your sterling silver worth?",
    lede: "Sterling flatware, .925 jewellery and old US coins. Enter the weight and this works out the pure silver content and what it is worth at today's spot price.",
    seoDescription: "Free silver calculator for sterling flatware, .925 jewellery, coin silver and junk silver. Live silver price, pure silver content, and melt value.",
    fallback: [
      { value: "925", label: "Sterling (.925)", factor: 0.925 },
      { value: "900", label: "Coin silver (.900)", factor: 0.9 },
      { value: "800", label: "Continental (.800)", factor: 0.8 },
      { value: "999", label: "Fine silver (.999)", factor: 0.999 },
    ],
    defaultPurity: "925",
    notes: [
      ["Flatware is heavier than it looks", "A full service for eight in sterling usually runs 1,500 to 2,500 grams. Knife handles are weighted with pitch or plaster and only the silver shell counts."],
      ["Silver plate is not silver", "EPNS, A1, triple plate and IS all mean plated. The silver layer weighs a fraction of a gram and there is no melt value in it."],
      ["US coins", "Dimes, quarters and half dollars dated 1964 or earlier are 90 percent silver. Half dollars from 1965 to 1970 are 40 percent."],
    ],
    faqs: [
      { question: "How much silver is in a sterling spoon?", answer: "A teaspoon is typically 15 to 25 grams gross, so around 14 to 23 grams of pure silver. A dinner fork runs 45 to 70 grams gross." },
      { question: "Is my flatware sterling or plate?", answer: "Sterling is marked STERLING, .925 or 925. If it says EPNS, A1, triple plate, IS or nothing at all, it is almost certainly plated." },
      { question: "What is junk silver?", answer: "Circulated pre-1965 US dimes, quarters and half dollars, bought purely for their 90 percent silver content rather than as collectables." },
    ],
  },

  platinum: {
    label: "Platinum",
    title: "Platinum calculator",
    heading: "What is your platinum worth?",
    lede: "Ring settings, wedding bands and lab scrap. Enter the weight and the mark and this works out the platinum content at today's spot price.",
    seoDescription: "Free platinum calculator. Work out the pure platinum content of PLAT, 950 and 900 marked jewellery and what it is worth at the live platinum price.",
    fallback: [
      { value: "950", label: "PLAT or 950", factor: 0.95 },
      { value: "900", label: "900", factor: 0.9 },
      { value: "999", label: "Fine platinum (.999)", factor: 0.999 },
    ],
    defaultPurity: "950",
    notes: [
      ["Platinum is dense", "It is about 11 percent heavier than 18K gold for the same size, which is why a plain platinum band feels substantial in the hand."],
      ["Common marks", "PLAT, PT, PT950, 950, 900PT and IRIDPLAT. White gold is marked with a karat instead and is worth considerably less."],
      ["It does not tarnish", "Platinum that has gone dull is scratched, not tarnished. That has no effect on what the metal is worth."],
    ],
    faqs: [
      { question: "Is platinum worth more than gold?", answer: "Per ounce it has been worth less than gold for most of the last decade, but a platinum ring usually contains more grams of precious metal than the same gold ring, and it is a higher purity." },
      { question: "How do I tell platinum from white gold?", answer: "Check the mark. Platinum reads PLAT, PT or 950. White gold reads 14K, 18K, 585 or 750. White gold is also usually rhodium plated, so it looks brighter when new." },
    ],
  },
};

////////// 2. ROW //////////

const blankRow = (purity) => ({ purity, weight: "", unit: "g" });

function Row({ row, purities, onChange, onRemove, canRemove, spotPerGram }) {
  const factor = purities.find((entry) => entry.value === row.purity)?.factor || 0;
  const fine = toGrams(row.weight, row.unit) * factor;
  const value = fine * (spotPerGram || 0);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_0.9fr_auto] gap-3 items-end py-4 border-b border-rule-soft last:border-0">
      <Select
        label="Purity"
        wrapClassName="col-span-2 sm:col-span-1"
        value={row.purity}
        onChange={(event) => onChange({ ...row, purity: event.target.value })}
        options={purities.map((entry) => ({ value: entry.value, label: entry.label }))}
      />
      <Input
        label="Weight"
        type="number"
        min="0"
        step="0.01"
        inputMode="decimal"
        placeholder="0"
        value={row.weight}
        onChange={(event) => onChange({ ...row, weight: event.target.value })}
      />
      <Select
        label="Unit"
        value={row.unit}
        onChange={(event) => onChange({ ...row, unit: event.target.value })}
        options={UNITS.map((unit) => ({ value: unit.value, label: unit.short }))}
      />
      <div className="flex items-center gap-2 justify-end pb-1.5 col-span-2 sm:col-span-1 sm:pb-2">
        <span className="num font-semibold text-ink text-right flex-1 sm:min-w-[86px]">
          {value > 0 ? moneyExact(value) : <span className="text-faint">—</span>}
        </span>
        {canRemove && (
          <button type="button" onClick={onRemove} className="btn btn-ghost !px-1.5" aria-label="Remove row">
            <Trash size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

////////// 3. PAGE //////////

export default function Calculator({ metal }) {
  const config = METALS[metal];
  const { spot } = useSpot();
  const { data: settings } = useAsync(() => publicApi.settings(), []);

  const purities = settings?.purities?.[metal]?.length ? settings.purities[metal] : config.fallback;
  const [rows, setRows] = useState([blankRow(config.defaultPurity)]);
  const Drawing = ART[metal] || ChainCoil;

  const totals = useMemo(() => {
    const fine = rows.reduce((sum, row) => {
      const factor = purities.find((entry) => entry.value === row.purity)?.factor || 0;
      return sum + toGrams(row.weight, row.unit) * factor;
    }, 0);
    const gross = rows.reduce((sum, row) => sum + toGrams(row.weight, row.unit), 0);
    return { fine, gross, value: fine * (spot?.perGram?.[metal] || 0) };
  }, [rows, purities, spot, metal]);

  // Once per visit, the first time there is a weight to work from. Looking at
  // the page is already a page view; this is the one that means somebody has
  // their jewellery and a scale in front of them.
  const used = useRef(false);
  useEffect(() => {
    if (used.current || !(totals.gross > 0)) return;
    used.current = true;
    analytics.trackToolUsed(`${metal}_calculator`, { metal });
  }, [totals.gross, metal]);

  const path = `/tools/${metal}-calculator`;

  return (
    <>
      <Seo title={config.title} path={path} description={config.seoDescription} image={SITE.ogTools} />
      <JsonLd data={breadcrumbSchema([
        { label: "Home", to: "/" },
        { label: "Calculators", to: "/tools" },
        { label: config.title, to: path },
      ])} />
      <JsonLd data={faqSchema(config.faqs)} />
      {/* A calculator is a tool, not an article. Saying so is what puts it in
          the results for "gold calculator" rather than only for "gold". */}
      <JsonLd data={toolSchema({ name: config.title, description: config.seoDescription, path })} />

      <div className="shell py-10 sm:py-12 md:py-16">
        <div className="grid gap-9 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16">
          <div>
            <Reveal as="div" from="none" className="flex items-center gap-3 mb-4">
              <p className="eyebrow">{config.label} calculator</p>
              <Drawing className="w-11 -my-2" />
            </Reveal>
            <Reveal as="h1" delay={60} className="display display-xl text-ink">{config.heading}</Reveal>
            <Reveal as="p" delay={130} className="lede mt-5 sm:mt-6 measure text-muted">{config.lede}</Reveal>

            <Reveal as={Panel} delay={190} className="mt-8">
              <div className="px-4 sm:px-6 pt-2 pb-1">
                {rows.map((row, index) => (
                  <Row
                    key={index}
                    row={row}
                    purities={purities}
                    spotPerGram={spot?.perGram?.[metal]}
                    canRemove={rows.length > 1}
                    onChange={(next) => setRows(rows.map((entry, i) => (i === index ? next : entry)))}
                    onRemove={() => setRows(rows.filter((_, i) => i !== index))}
                  />
                ))}
              </div>

              <div className="px-4 sm:px-6 py-3 border-t border-rule-soft">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRows([...rows, blankRow(config.defaultPurity)])}>
                  <Plus size={15} /> Add another karat or weight
                </button>
              </div>

              <div className="px-4 sm:px-6 py-5 bg-surface border-t border-rule grid grid-cols-2 sm:grid-cols-3 gap-4 sm:items-end">
                <div>
                  <p className="eyebrow">Gross weight</p>
                  <p className="display display-sm text-ink mt-1 num">{grams(totals.gross)}</p>
                </div>
                <div>
                  <p className="eyebrow">Pure {config.label.toLowerCase()}</p>
                  <p className="display display-sm text-ink mt-1 num">{grams(totals.fine, 3)}</p>
                  <p className="hint num">{ounces(totals.fine)}</p>
                </div>
                <div className="col-span-2 sm:col-span-1 sm:text-right border-t sm:border-0 border-rule pt-4 sm:pt-0">
                  <p className="eyebrow">Metal content value</p>
                  <p className="display display-md text-ink mt-1">
                    {totals.value > 0
                      ? <CountUp to={totals.value} duration={550} format={(value) => money(value)} />
                      : <span className="num">&mdash;</span>}
                  </p>
                </div>
              </div>
            </Reveal>

            <p className="hint mt-4 leading-relaxed measure-wide">
              This is the metal content at spot, which is the figure every buyer starts from.
              A real offer sits below it because the metal still has to be refined, insured and shipped.
              We show you both numbers on your own order, so you can check ours against anyone else&rsquo;s.
            </p>

            <div className="flex flex-col xs:flex-row flex-wrap gap-3 mt-7">
              <Link to="/get-a-kit" className="btn btn-primary">Get a free kit <ArrowRight size={16} /></Link>
              <Link to="/tools" className="btn btn-quiet">Other calculators</Link>
            </div>
          </div>

          <aside className="lg:pt-16">
            <Reveal as={Panel} from="right" quiet className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Scale size={17} className="text-bronze" />
                <p className="eyebrow">Today&rsquo;s {config.label.toLowerCase()} price</p>
              </div>
              <p className="display display-md text-ink num">
                {spot?.perOunce?.[metal] ? money(spot.perOunce[metal]) : "—"}
                <span className="text-[13px] font-sans text-muted font-normal ml-1.5">per troy ounce</span>
              </p>
              <p className="hint mt-1 num">
                {spot?.perGram?.[metal] ? `${moneyExact(spot.perGram[metal])} per gram` : ""}
              </p>
              {spot?.stale && (
                <Alert tone="warning" className="mt-3">
                  This price is not updating right now. Treat it as approximate.
                </Alert>
              )}
              <p className="hint mt-3 pt-3 border-t border-rule">
                One troy ounce is {GRAMS_PER_OZT.toFixed(3)} grams, not the {(28.3495).toFixed(3)} grams
                of the ounce on a kitchen scale.
              </p>
            </Reveal>

            <StaggerBoxed as="div" step={90} className="mt-8 space-y-6">
              {config.notes.map(([title, body]) => (
                <div key={title}>
                  <h2 className="display display-sm text-ink">{title}</h2>
                  <p className="hint mt-1.5 leading-relaxed">{body}</p>
                </div>
              ))}
            </StaggerBoxed>
          </aside>
        </div>

        {/* Questions, which is also the FAQ structured data above. */}
        <section className="mt-14 md:mt-24 border-t border-rule pt-10 md:pt-12">
          <Reveal as="h2" className="display display-lg text-ink max-w-2xl">Questions about {config.label.toLowerCase()}</Reveal>
          <StaggerBoxed as="div" step={100} className="grid gap-7 md:grid-cols-3 mt-8">
            {config.faqs.map((faq) => (
              <div key={faq.question}>
                <h3 className="display display-sm text-ink">{faq.question}</h3>
                <p className="hint mt-2 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </StaggerBoxed>
        </section>
      </div>

      <div className="shell pb-10">
        <EmbedThis tool="calculator" metal={metal} />
      </div>

      <MobileCta />

      <CtaBand
        title={`Find out what your ${config.label.toLowerCase()} is really worth.`}
        body="The calculator gives you the metal content. A free insured kit gives you a real, itemised offer with the video of us weighing every piece."
      />
    </>
  );
}
